import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBookOpen,
    faBuilding,
    faClipboardCheck,
    faUser,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';

export enum WizardStepId {
    BusinessProfile = 'business-profile',
    BorrowerEntity = 'borrower-entity',
    Owners = 'owners',
    OtherOfficers = 'other-officers',
    KeyEmployees = 'key-employees',
    References = 'references',
    Review = 'review',
}

export type StepScope = 'business' | 'entity';

export interface WizardStepDefinition {
    id: WizardStepId;
    path: string;
    label: string;
    heading?: string;
    icon?: IconDefinition;
    scope: StepScope;
    canSkip: boolean;
}

export const ALL_WIZARD_STEPS: WizardStepDefinition[] = [
    {
        id: WizardStepId.BusinessProfile,
        path: 'business-profile',
        label: 'Business Profile',
        icon: faBuilding,
        scope: 'business',
        canSkip: false,
    },
    {
        id: WizardStepId.BorrowerEntity,
        path: 'borrower-entity',
        label: 'Borrower Entity',
        heading: 'Add Entity',
        icon: faUsers,
        scope: 'business',
        canSkip: false,
    },
    {
        id: WizardStepId.Owners,
        path: 'owners',
        label: 'Owners of Borrower Entity',
        icon: faUsers,
        scope: 'entity',
        canSkip: false,
    },
    {
        id: WizardStepId.OtherOfficers,
        path: 'other-officers',
        label: 'Other Officers',
        icon: faUser,
        scope: 'entity',
        canSkip: false,
    },
    {
        id: WizardStepId.KeyEmployees,
        path: 'key-employees',
        label: 'Key Employees',
        icon: faUser,
        scope: 'entity',
        canSkip: false,
    },
    {
        id: WizardStepId.References,
        path: 'references',
        label: 'References',
        heading: 'Add Reference',
        icon: faBookOpen,
        scope: 'business',
        canSkip: true,
    },
    {
        id: WizardStepId.Review,
        path: 'review',
        label: 'Review',
        heading: 'Review / Update',
        icon: faClipboardCheck,
        scope: 'business',
        canSkip: false,
    },
];

// TODO: Only these steps are currently active. The rest are in development and will be added to the
// release when ready.
const RELEASED_STEP_IDS: ReadonlySet<WizardStepId> = new Set([
    WizardStepId.BusinessProfile,
    WizardStepId.BorrowerEntity,
]);

export const WIZARD_STEPS: WizardStepDefinition[] = ALL_WIZARD_STEPS.filter((step) =>
    RELEASED_STEP_IDS.has(step.id),
);

export const findStepByPath = (path: string | undefined): WizardStepDefinition | undefined =>
    WIZARD_STEPS.find((step) => step.path === path);

export const findStepById = (id: WizardStepId): WizardStepDefinition | undefined =>
    WIZARD_STEPS.find((step) => step.id === id);

export const stepIndex = (id: WizardStepId): number => WIZARD_STEPS.findIndex((step) => step.id === id);

export const nextStep = (id: WizardStepId): WizardStepDefinition | undefined =>
    WIZARD_STEPS[stepIndex(id) + 1];

export const previousStep = (id: WizardStepId): WizardStepDefinition | undefined => {
    const index = stepIndex(id);
    return index > 0 ? WIZARD_STEPS[index - 1] : undefined;
};

export const WIZARD_BASE_PATH = '/business/wizard';

export const stepUrl = (step: WizardStepDefinition): string => `${WIZARD_BASE_PATH}/${step.path}`;
